<template>
    <div class="pie-setup">
        <div v-if="(campaign.attribution_provider === 'AppsFlyer' || campaign.redirect_test_attribution_provider === 'AppsFlyer') && campaign.billing_type_id === 4">
            <b-field>
                <template v-slot:label>
                    {{ $t('post_install_event_name') }} *
                    <b-tooltip type="is-dark" :label="$t('cpa_pie_name_tooltip')" multilined>
                        <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                </template>

                <div v-if="campaign.attribution_provider === 'AppsFlyer'">
                    <div class="bottom-spacer-20">
                        <span>{{ $t('appsflyer_cpa_help_text') }}</span>
                        <ol>
                            <li>{{ $t('general_pie_help_text_1') }}</li>
                            <li>{{ $t('appsflyer_pie_help_text_2') }}</li>
                            <li>{{ $t('appsflyer_pie_help_text_3') }}</li>
                            <li>{{ $t('appsflyer_pie_help_text_4') }}</li>
                        </ol>
                    </div>

                    <div class="pie-table">
                        <div v-for="(event, index) in campaign.post_install_events" :key="index" class="pie-row">
                            <b-field class="pie-cell">
                                <template v-slot:label v-if="index === 0">
                                    {{ $t('sdk_event_name') }}
                                </template>
                                <b-input v-model="event.event_name" required :disabled="disabled"></b-input>
                            </b-field>

                            <b-field class="pie-cell">
                                <template v-slot:label v-if="index === 0">
                                    {{ $t('partner_event_identifier') }}
                                </template>
                                <span>{{ index + 1 | generateAppsFlyerPartnerEventIdentifier }}</span>
                            </b-field>
                        </div>
                    </div>
                </div>
            </b-field>
        </div>

        <div v-if="(campaign.attribution_provider !== 'TrafficGuard' && campaign.attribution_provider !== 'Interceptd' || campaign.redirect_test_attribution_provider !== undefined) && campaign.attribution_provider !== 'Tenjin' && campaign.billing_type_id !== 4" class="bottom-spacer-20">
            <b-field>
                <template v-slot:label>
                    {{ $t('post_install_event_optimization') }}
                </template>
                <div>
                    <div v-if="campaign.attribution_provider === 'AppsFlyer' || campaign.redirect_test_attribution_provider === 'AppsFlyer'">
                        <div class="bottom-spacer-20">
                            <span>{{ $t('general_cpi_pie_help_text') + $t('appsflyer_cpi_pie_help_text') }}</span>
                            <ol>
                                <li>{{ $t('general_pie_help_text_1') }}</li>
                                <li>{{ $t('appsflyer_pie_help_text_2') }}</li>
                                <li>{{ $t('appsflyer_pie_help_text_3') }}</li>
                                <li>{{ $t('appsflyer_pie_help_text_4') }}</li>
                            </ol>
                        </div>
                        <div class="pie-table">
                            <div v-for="(event, index) in campaign.post_install_events" :key="index" class="pie-row">
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('sdk_event_name') }}
                                    </template>
                                    <b-input v-model="event.event_name" :disabled="isEventIdentityDisabled(event)"></b-input>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('partner_event_identifier') }}
                                    </template>
                                    <span>{{ index + 1 | generateAppsFlyerPartnerEventIdentifier }}</span>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('target_percentage') }}
                                        <b-tooltip type="is-dark" :label="$t('target_percentage_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <div class="inline">
                                        <b-radio v-model="campaign.target_post_install_event" :native-value="event.event_name" :disabled="disabled"/>
                                        <b-input icon="percent" v-model="event.target_percentage" :disabled="disabled || campaign.target_post_install_event !== event.event_name" @input="clearEventValue(index)" type="number" min="0.01" max="100" step="0.01" style="min-width: 80px"></b-input>
                                    </div>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_value') }}
                                        <b-tooltip type="is-dark" :label="$t('event_value_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="event.event_value" icon="currency-usd" type="number" max="9999.999" step="0.001" :disabled="isEventValueDisabled(event)" @input="clearTargetPercentage(index)"></b-input>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_budget') }}
                                        <b-tooltip type="is-dark" :label="$t('event_budget_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="event.event_budget" icon="currency-usd" type="number" max="9999999.99" step="0.01" :disabled="isEventValueDisabled(event)" @input="clearTargetPercentage(index)"></b-input>
                                </b-field>
                                <b-field class="pie-cell pie-action">
                                    <b-button class="is-small" @click="deletePostInstallEvent(index)" :disabled="disabled">{{$t('remove')}}</b-button>
                                </b-field>
                            </div>
                        </div>
                        <div v-if="campaign.post_install_events.length === 0">
                            <b-button class="is-small" @click="addPostInstallEvent()" :disabled="disabled">{{ $t('add_post_install_event') }}</b-button>
                        </div>
                        <div v-else-if="appsFlyerPostInstallEventsCompleted">
                            <b-button class="is-small" @click="addPostInstallEvent()" :disabled="disabled">{{ $t('add_another_post_install_event') }}</b-button>
                        </div>
                    </div>

                    <div v-else-if="campaign.attribution_provider === 'Adjust' || campaign.redirect_test_attribution_provider === 'Adjust'">
                        <div class="bottom-spacer-20">
                            <span>{{ $t('general_cpi_pie_help_text') }}</span>
                            <ol>
                                <li>{{ $t('adjust_pie_help_text_1') }}</li>
                                <li>{{ $t('adjust_pie_help_text_2') }}</li>
                                <li>{{ $t('adjust_pie_help_text_3') }}</li>
                            </ol>
                        </div>
                        <div class="pie-table">
                            <div v-for="(event, index) in campaign.post_install_events" :key="index" class="pie-row">
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('sdk_event_name') }}
                                    </template>
                                    <b-input v-model="event.event_name" :disabled="isEventIdentityDisabled(event)" @blur="lookupAdvertiserEventIds" @keyup.native.enter="lookupAdvertiserEventIds"></b-input>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('sdk_event_token') }}
                                    </template>
                                    <b-input v-model="event.event_token" :disabled="isEventIdentityDisabled(event)"></b-input>
                                </b-field>
                                <b-field class="pie-cell pie-callback">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_callback') }}
                                    </template>
                                    <span class="wrap-span">{{generateAdjustPartnerEventCallback(event)}}</span>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('target_percentage') }}
                                        <b-tooltip type="is-dark" :label="$t('target_percentage_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <div class="inline">
                                        <b-radio v-model="campaign.target_post_install_event" :native-value="event.event_name" :disabled="disabled"/>
                                        <b-input icon="percent" v-model="event.target_percentage" :disabled="disabled || campaign.target_post_install_event !== event.event_name" @input="clearEventValue(index)" type="number" min="0.01" max="100" step="0.01" style="min-width: 80px"></b-input>
                                    </div>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_value') }}
                                        <b-tooltip type="is-dark" :label="$t('event_value_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="event.event_value" icon="currency-usd" type="number" max="9999.999" step="0.001" :disabled="isEventValueDisabled(event)" @input="clearTargetPercentage(index)"></b-input>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_budget') }}
                                        <b-tooltip type="is-dark" :label="$t('event_budget_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="event.event_budget" icon="currency-usd" type="number" max="9999999.99" step="0.01" :disabled="isEventValueDisabled(event)" @input="clearTargetPercentage(index)"></b-input>
                                </b-field>
                                <b-field class="pie-cell pie-action">
                                    <b-button class="is-small" @click="deletePostInstallEvent(index)" :disabled="disabled">{{$t('remove')}}</b-button>
                                </b-field>
                            </div>
                        </div>
                        <div v-if="campaign.post_install_events.length === 0">
                            <b-button class="is-small" @click="addPostInstallEvent()" :disabled="disabled">{{ $t('add_post_install_event') }}</b-button>
                        </div>
                        <div v-else-if="adjustPostInstallEventsCompleted && !adjustPostInstallEventsMaxedOut">
                            <b-button class="is-small" @click="addPostInstallEvent()" :disabled="disabled">{{ $t('add_another_post_install_event') }}</b-button>
                        </div>
                    </div>

                    <div v-else>
                        <div class="bottom-spacer-20">
                            <span>{{ $t('general_cpi_pie_help_text') }}</span>
                            <ol>
                                <li>{{ $t('general_pie_help_text_1') }}</li>
                                <li>{{ $t('general_pie_help_text_2') }}</li>
                                <li>{{ $t('general_pie_help_text_3') }}</li>
                            </ol>
                            <span>{{ $t('general_pie_help_text_4') }} <a href="mailto:tams@sift.co" target="_blank">tams@sift.co</a> {{ $t('general_pie_help_text_5') }}</span>
                        </div>
                        <div class="pie-table">
                            <div v-for="(event, index) in campaign.post_install_events" :key="index" class="pie-row">
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('sdk_event_name') }}
                                    </template>
                                    <b-input v-model="event.event_name" :disabled="isEventIdentityDisabled(event)" @blur="lookupAdvertiserEventIds" @keyup.native.enter="lookupAdvertiserEventIds"></b-input>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('target_percentage') }}
                                        <b-tooltip type="is-dark" :label="$t('target_percentage_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <div class="inline">
                                        <b-radio v-model="campaign.target_post_install_event" :native-value="event.event_name" :disabled="disabled"/>
                                        <b-input icon="percent" v-model="event.target_percentage" :disabled="disabled || campaign.target_post_install_event !== event.event_name" @input="clearEventValue(index)" type="number" min="0.01" max="100" step="0.01" style="min-width: 80px"></b-input>
                                    </div>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_value') }}
                                        <b-tooltip type="is-dark" :label="$t('event_value_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="event.event_value" icon="currency-usd" type="number" max="9999.999" step="0.001" :disabled="isEventValueDisabled(event)" @input="clearTargetPercentage(index)"></b-input>
                                </b-field>
                                <b-field class="pie-cell">
                                    <template v-if="index === 0" v-slot:label>
                                        {{ $t('event_budget') }}
                                        <b-tooltip type="is-dark" :label="$t('event_budget_tooltip')" multilined>
                                            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="event.event_budget" icon="currency-usd" type="number" max="9999999.99" step="0.01" :disabled="isEventValueDisabled(event)" @input="clearTargetPercentage(index)"></b-input>
                                </b-field>
                                <b-field class="pie-cell pie-action">
                                    <b-button class="is-small" @click="deletePostInstallEvent(index)" :disabled="disabled">{{$t('remove')}}</b-button>
                                </b-field>
                            </div>
                        </div>

                        <div v-if="campaign.post_install_events.length === 0">
                            <b-button class="is-small" @click="addPostInstallEvent()" :disabled="disabled">{{ $t('add_post_install_event') }}</b-button>
                        </div>
                        <div v-else-if="generalPostInstallEventsCompleted">
                            <b-button class="is-small" @click="addPostInstallEvent()" :disabled="disabled">{{ $t('add_another_post_install_event') }}</b-button>
                        </div>
                    </div>
                </div>
            </b-field>
        </div>
    </div>
</template>

<style>
label {
    white-space: nowrap;
}
.pie-setup {
    max-width: 100%;
    min-width: 0;
}
.pie-setup label,
.pie-setup .label {
    white-space: normal;
}
.pie-table {
    display: block;
    max-width: 100%;
    min-width: 0;
    margin-bottom: 10px;
}
.pie-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px 16px;
    max-width: 100%;
    min-width: 0;
    margin-bottom: 12px;
}
.pie-cell {
    display: block;
    flex: 1 1 160px;
    min-width: 0;
    max-width: 100%;
    margin-bottom: 0 !important;
    box-sizing: border-box;
}
.pie-cell.pie-callback {
    flex: 1 1 240px;
}
.pie-cell.pie-action {
    flex: 0 0 auto;
}
.pie-cell .control,
.pie-cell .input,
.pie-cell .select {
    width: 100%;
    max-width: 100%;
    min-width: 0;
}
.pie-cell .inline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    min-width: 0;
}
.pie-cell .inline .control {
    flex: 1 1 80px;
    width: auto;
}
.bottom-spacer-20 {
    margin-bottom: 20px;
}
.wrap-span {
    display: block;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-wrap: break-word;
    white-space: normal;
}
</style>

<script>
import session from "../session";

const model = {
    advertiserEventIds: {}
};

function errorHandler() {
}

export default {
    props: {
        campaign: Object,
        clone:  Boolean,
        disabled: Boolean,
        isSuperUser: Boolean
    },

    data() {
        return model;
    },

    computed: {
        appsFlyerPostInstallEventsCompleted: function () {
            var completed = true;
            if (this.campaign.post_install_events) {
                for (let i = 0; i < this.campaign.post_install_events.length; i++) {
                    if (!this.campaign.post_install_events[i].event_name || this.campaign.post_install_events[i].event_name === '') {
                        completed = false;
                    }
                }
            }
            return completed;
        },
        adjustPostInstallEventsCompleted: function () {
            var completed = true;
            if (this.campaign.post_install_events) {
                for (let i = 0; i < this.campaign.post_install_events.length; i++) {
                    if (!this.campaign.post_install_events[i].event_name || this.campaign.post_install_events[i].event_name === '' || !this.campaign.post_install_events[i].event_token || this.campaign.post_install_events[i].event_token === '') {
                        completed = false;
                    }
                }
            }
            return completed;
        },
        adjustPostInstallEventsMaxedOut: function () {
            return this.campaign.post_install_events && this.campaign.post_install_events.length >= 2;
        },
        generalPostInstallEventsCompleted: function () {
            var completed = true;
            if (this.campaign.post_install_events) {
                for (let i = 0; i < this.campaign.post_install_events.length; i++) {
                    if (!this.campaign.post_install_events[i].event_name || this.campaign.post_install_events[i].event_name === '') {
                        completed = false;
                    }
                }
            }
            return completed;
        }
    },

    filters: {
        generateAppsFlyerPartnerEventIdentifier: function (goalId) {
            if (goalId) {
                return '$$click(goalid_' + goalId + ')';
            } else {
                return '';
            }
        }
    },

    methods: {
        // Partners cannot change saved event names/tokens, or start event-value/budget
        // optimization after 10 LTD installs. Super users (master-password / AM login) may.
        isEventIdentityDisabled: function (event) {
            if (this.disabled) {
                return true;
            }
            if (this.isSuperUser || this.clone) {
                return false;
            }
            return !!(this.campaign.offer_id && event.id);
        },
        isEventValueDisabled: function (event) {
            if (this.disabled) {
                return true;
            }
            if (this.clone) {
                return false;
            }
            if (this.campaign.target_post_install_event !== event.event_name) {
                return true;
            }
            if (this.isSuperUser) {
                return false;
            }
            return this.campaign.installs_ltd > 10 && !event.event_value && !event.event_budget;
        },
        addPostInstallEvent: function () {
            this.campaign.post_install_events.push({
                event_id: null,
                event_token: null,
                event_name: null,
                target_percentage: null,
                event_value: null,
                event_budget: null
            });
        },
        lookupAdvertiserEventIdsSuccessHandler: function (responseData) {
            if (responseData) {
                this.campaign.post_install_events = responseData.post_install_events;
            }
        },
        lookupAdvertiserEventIds: function () {
            const data = {
                post_install_events: this.campaign.post_install_events
            };
            session.post('advertiser_event_ids', data, this.lookupAdvertiserEventIdsSuccessHandler, errorHandler, []);
        },
        clearEventValue: function (index) {
            this.campaign.post_install_events[index].event_value = null;
            this.campaign.post_install_events[index].event_budget = null;
        },
        clearTargetPercentage: function (index) {
            this.campaign.post_install_events[index].target_percentage = null;
        },
        deletePostInstallEvent: function (index) {
            this.campaign.post_install_events.splice(index, 1);
        },
        generateAdjustPartnerEventCallback: function (event) {
            if (event.event_name && event.event_token) {
                const callbackUrl = 'https%3A%2F%2Fvapinteg.prod.sift.co%2Fpost_install%3Fprovider%3DDemandScale%26provider_id%3D267%26mapped_params%3D%7Btransaction_id%7D%26event_type%3D' + event.event_name + '%26ts%3D%7Bcreated_at%7D%26event_token%3D' + event.event_token + '%26gps_adid%3D%7Bgps_adid%7D%26idfa%3D%7Bidfa%7D%26app_name%3D%7Bapp_name%7D%26activity_kind%3D%7Bactivity_kind%7D%26created_at%3D%7Bcreated_at%7D';
                if (this.campaign.attribution_provider === 'Adjust') {
                    return 'event_callback_' + event.event_token + '=' + callbackUrl;
                }
                else if (this.campaign.redirect_test_attribution_provider === 'Adjust') {
                    return 'adj_event_callback_' + event.event_token + '=' + callbackUrl;
                }
                else {
                    return '';
                }
            } else {
                return '';
            }
        }
    }
}
</script>
